import { HOURS_IN_DAY } from '@stdlib/constants-time';
import { Form, Layout, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  AvailablityRestrictions,
  DEFAULT_AVAILABLE_ON,
  DEFAULT_CHECKIN,
  DEFAULT_CHECKOUT,
  EARLY_CHECKIN,
  LATE_CHECKOUT,
  weekDayArray,
} from 'app/components/commons/Experience/Availability/utils';
import type { ExperienceFormValues } from 'app/components/commons/Experience/ExperienceForm';
import { ExperienceForm } from 'app/components/commons/Experience/ExperienceForm';
import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { Mode } from 'app/components/commons/Hotel/Types';
import type { FormOpeningHours } from 'app/components/commons/TimeRangeSection/utils';
import {
  formatDateRangeForAPI,
  formatOpeningHoursForAPI,
  parseDateRangeFromAPI,
  parseOpeningHoursFromAPI,
} from 'app/components/commons/TimeRangeSection/utils';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import {
  useGetExperiencesById,
  useSaveExperience,
} from 'app/hooks/data/useExperience';
import type { DateRange } from 'app/redux/models/Experience/Experience';
import type { Experience } from 'app/typings/experiences';
import { dayjs } from 'app/utils/dayjs';
import { useSearchParams } from 'app/utils/searchParams';

import './ExperienceDetails.scss';

const initialValues: Experience = {
  id: 0,
  categoryId: 0,
  description: null,
  hotelId: undefined,
  countryId: undefined,
  included: false,
  name: null,
  pictures: [],
  coverPicture: undefined,
  price: 0,
  priceDescription: null,
  purchasePrice: undefined,
  publicPrice: undefined,
  openingHours: null,
  brand: null,
  published: false,
  spaceIds: [],
  accessMode: null,
  emoji: null,
  roomServiceIncluded: false,
  roomServiceAvailable: false,
  roomServicePrice: null,
  quantity: null,
  quantityDescription: null,
  duration: null,
  durationDescription: null,
  time: null,
  locations: null,
  closingPeriods: [],
  closedHoursBeforeCheckin: null,
  addOnLimitAvailabilityUnit: 'hours',
  maxQuantity: null,
  availableDays: [],
  availableDates: [],
  availableDayLimits: DEFAULT_AVAILABLE_ON,
  isAlwaysIncludedWithNight: false,
};

export const ExperienceDetails = () => {
  const history = useHistory();
  const { id: experienceId } = useParams<{ id: string }>();
  const { getSearchParam } = useSearchParams();

  const hotelId = getSearchParam('hotelId');

  const { data: experience } = useGetExperiencesById(
    !!experienceId ? +experienceId : undefined
  );
  const { mutateAsync: saveExperience } = useSaveExperience();

  const experienceWithSpaces = useMemo(
    () =>
      !!experienceId && experience
        ? {
            ...experience,
            spaceIds:
              experience.spaceIds.length === 0
                ? [NOT_APPLICABLE_SPACE_ID]
                : experience.spaceIds,
          }
        : initialValues,
    [experience, experienceId]
  );

  const [mode, setMode] = useState<Mode>(
    !!experienceId ? Mode.View : Mode.Edit
  );

  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const onCancel = useCallback(() => {
    if (experienceId) {
      setMode(Mode.View);
      form.validateFields();
    } else {
      history.push('/activities');
    }
  }, [experienceId, form, history]);

  const onFinish = useCallback(
    async (values: ExperienceFormValues) => {
      if (
        (values.duration && !values.durationDescription) ||
        (!values.duration && values.durationDescription)
      ) {
        message.error('Duration needs time value and a unit of time');

        return;
      }

      if (
        (values.price && !values.priceDescription) ||
        (!values.price && values.priceDescription)
      ) {
        message.error('Price needs value and a price description');

        return;
      }

      if (
        (values.quantity && !values.quantityDescription) ||
        (!values.quantity && values.quantityDescription)
      ) {
        message.error('Quantity needs value and a quantity description');

        return;
      }

      if (
        (values.purchasePrice || values.publicPrice) &&
        values.priceDescription === null
      ) {
        message.error(
          'Purchase and public price needs value and a price description'
        );

        return;
      }

      const hasEveryDay =
        values.availableDays &&
        weekDayArray.every((day) => values.availableDays.includes(day));

      const formatted: ExperienceFormValues = {
        ...experienceWithSpaces,
        ...values,
        openingHours: values.openingHours
          ? formatOpeningHoursForAPI(
              values.openingHours as unknown as FormOpeningHours
            )
          : null,
        time: values.time ? dayjs(values.time).format('HH:mm') : null,
        roomServiceAvailable: values.roomServiceAvailable || false,
        roomServiceIncluded: values.roomServiceIncluded || false,
        id: experienceWithSpaces?.id || 0,
        published:
          experienceWithSpaces?.published === undefined
            ? true
            : experienceWithSpaces.published,
        spaceIds:
          values.spaceIds?.filter(
            (s: unknown) => s !== NOT_APPLICABLE_SPACE_ID
          ) || [],
        coverPicture: values.pictures[0],
        price: values.price || 0,
        closingPeriods: formatDateRangeForAPI(
          values.closingPeriods
        ) as unknown as [Date, Date][],
        closedHoursBeforeCheckin: values.closedHoursBeforeCheckin
          ? values.addOnLimitAvailabilityUnit === 'hours'
            ? values.closedHoursBeforeCheckin
            : values.closedHoursBeforeCheckin * HOURS_IN_DAY
          : null,
        maxQuantity: values.maxQuantity || null,
        availableDays:
          values.availableDaysRadio !== AvailablityRestrictions.SpecificDays ||
          hasEveryDay
            ? []
            : values.availableDays,
        availableDayLimits: values.availableOn,
        availableDates:
          values.availableDaysRadio !== AvailablityRestrictions.SpecificDates
            ? []
            : values.availableDates,
        isAlwaysIncludedWithNight: values.isAlwaysIncludedWithNight || false,
        menu: values.menu ?? null,
        treatmentList: values.treatmentList ?? null,
      };

      await saveExperience(formatted);
      setMode(Mode.View);

      history.push('/activities');
    },
    [experienceWithSpaces, history, saveExperience]
  );

  const formattedValues: ExperienceFormValues | null = useMemo(() => {
    if (!experienceWithSpaces) {
      return null;
    }

    const addOnLimitAvailabilityUnit =
      experienceWithSpaces.closedHoursBeforeCheckin &&
      experienceWithSpaces.closedHoursBeforeCheckin % HOURS_IN_DAY === 0
        ? 'days'
        : 'hours';

    const hasEveryDay =
      experienceWithSpaces.availableDays.length === 0 ||
      weekDayArray.every((day) =>
        experienceWithSpaces?.availableDays?.includes(day)
      );

    const hasDatesLimits = experienceWithSpaces.availableDates.length > 0;

    return {
      ...experienceWithSpaces,
      openingHours: parseOpeningHoursFromAPI(
        experienceWithSpaces.openingHours
      ) as unknown as Experience['openingHours'],
      pictures: experienceWithSpaces.pictures,
      closingPeriods: parseDateRangeFromAPI(
        (experienceWithSpaces.closingPeriods as unknown as DateRange[]).map(
          (date) => ({
            start: new Date(date.start),
            end: new Date(date.end),
          })
        )
      ),
      closedHoursBeforeCheckin:
        addOnLimitAvailabilityUnit === 'days' &&
        experienceWithSpaces.closedHoursBeforeCheckin
          ? experienceWithSpaces.closedHoursBeforeCheckin / HOURS_IN_DAY
          : experienceWithSpaces.closedHoursBeforeCheckin,
      addOnLimitAvailabilityUnit: addOnLimitAvailabilityUnit,
      availableDaysRadio: hasDatesLimits
        ? AvailablityRestrictions.SpecificDates
        : hasEveryDay
        ? AvailablityRestrictions.EveryDay
        : AvailablityRestrictions.SpecificDays,
      availableDays: experienceWithSpaces.availableDays ?? weekDayArray,
      availableOn: experienceWithSpaces.availableDayLimits,
      time: experienceWithSpaces.time
        ? dayjs(experienceWithSpaces.time, 'HH:mm')
        : null,
      hotelId: hotelId ? parseInt(hotelId) : experienceWithSpaces.hotelId,
    };
  }, [experienceWithSpaces, hotelId]);

  useEffect(() => {
    if (formattedValues !== null) {
      form.setFieldsValue(formattedValues);
    }
  }, [form, formattedValues]);

  useEffect(() => {
    if (!experienceId && formValues && formValues.categoryId) {
      if (formValues.categoryId === EARLY_CHECKIN) {
        form.setFieldsValue({ ...formValues, availableOn: DEFAULT_CHECKIN });
      } else if (formValues.categoryId === LATE_CHECKOUT) {
        form.setFieldsValue({ ...formValues, availableOn: DEFAULT_CHECKOUT });
      }
    }
  }, [form, formValues, experienceId]);

  if (!formattedValues) {
    return null;
  }

  return (
    <Layout className="master-page">
      <FormLegacyRenderProp
        form={form}
        onFinish={onFinish}
        initialValues={formattedValues}
        scrollToFirstError={{
          behavior: 'smooth',
          inline: 'center',
          block: 'center',
        }}
      >
        {(
          values,
          {
            resetFields,
            isFieldsTouched,
            submit,
            setFieldsValue,
            validateFields,
          }
        ) => (
          <ExperienceForm
            values={values}
            initialValues={formattedValues}
            setFieldsValue={setFieldsValue}
            resetFields={resetFields}
            isFieldsTouched={isFieldsTouched}
            submit={submit}
            onCancel={onCancel}
            mode={mode}
            setMode={setMode}
            experienceId={+experienceId}
            isFormValid={form
              .getFieldsError()
              .every((item) => item.errors.length > 0)}
            validateFields={validateFields}
          />
        )}
      </FormLegacyRenderProp>
    </Layout>
  );
};
